<template>
  <main-drawer
    :title="`${$tc('general.registration_fee')}${title}`"
    :size="900"
    @close="$emit('close')"
  >
    <el-table :data="allData" style="width: 100%">
      <el-table-column :label="$tc('form.name')">
        <template slot-scope="scope">
          <div class="flex">
            <i class="el-icon-user mr-5" />
            <template v-if="scope.row.isTeam">
              <i class="el-icon-user mr-5" />
              <div>
                <div class="name">{{ scope.$index + 1 }}. {{ getCompetitorNames(scope.row) }}</div>
                <div v-if="showClubName && scope.row.club" class="club-name">{{ getClubLabel(scope.row.club) }}</div>
              </div>
            </template>
            <div v-else>
              <div class="name">{{ scope.$index + 1 }}. {{ getCompetitorDisplayName(scope.row) }}</div>
              <div v-if="showClubName && scope.row.club" class="club-name">{{ getClubLabel(scope.row.club) }}</div>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column :label="$tc('settings.discipline')" align="center">
        <template slot-scope="scope">
          <data-list :list="getDisciplinesDataList(scope.row.categories)" />
        </template>
      </el-table-column>

      <el-table-column :label="$tc('form.price')" align="right" width="200px">
        <template slot-scope="scope">
          {{
            scope.row.isTeam
              ? getTeamPriceText(scope.row, hasClubDiscount(scope.row))
              : getPriceText(scope.row, hasClubDiscount(scope.row))
          }}
          RSD
        </template>
      </el-table-column>
    </el-table>

    <div class="text">
      Cena bez popusta: <b>{{ priceOne }} RSD</b>
    </div>
    <div v-if="!costReduction" class="text">
      Cena sa popustom: <b>{{ priceTwo }} RSD</b>
    </div>
    <div v-if="clubForDiscount && getCostReduction(clubForDiscount)" class="text">
      Popust udruženja: <b>{{ getCostReduction(clubForDiscount) }}%</b> ({{ priceOne - total }} RSD)
    </div>
    <div class="text">
      Ukupno: <b>{{ total }} RSD</b>
    </div>

    <template v-slot:footer>
      <div class="flex-buttons">
        <el-button @click="$emit('close')">Zatvori</el-button>
        <el-button
          type="primary"
          icon="el-icon-printer"
          @click="printit(false)"
        >
          {{ $tc("general.print") }}
        </el-button>
        <el-button type="primary" icon="el-icon-printer" @click="printit(true)">
          {{ $tc("general.print_without_discount") }}
        </el-button>
      </div>
    </template>

    <print-registration-fee
      ref="printTable"
      v-show="false"
      :data="data"
      :tournament="tournament"
      :team-data="teamData"
      :show-club-name="showClubName"
      :hide-discount="hideDiscount"
    />
  </main-drawer>
</template>

<script>
import openPrint from '@/common/print'

import PrintRegistrationFee from './PrintRegistrationFee.vue'
import registrationFeeMixin from './registration-fee'

export default {
  mixins: [registrationFeeMixin],

  components: {
    PrintRegistrationFee
  },

  props: {
    title: {
      type: String,
      default: ''
    },

    data: {
      type: Array,
      required: true
    },

    teamData: {
      type: Array,
      required: true
    },
    tournament: {
      type: Object,
      required: true
    },

    club: {
      type: Object,
      default: null
    },
    showClubName: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      openPrint,
      hideDiscount: false
    }
  },

  computed: {
    allData () {
      const comps = Array.isArray(this.data) ? this.data : []
      const teams = Array.isArray(this.teamData) ? this.teamData : []
      return [
        ...[...comps].sort((a, b) => (parseInt(a.club?.id || 0) - parseInt(b.club?.id || 0))),
        ...teams.map((item) => ({
          ...item,
          isTeam: true
        }))
      ]
    },

    clubForDiscount () {
      return this.club || this.allData[0]?.club || null
    },

    costReduction () {
      return this.getCostReduction(this.clubForDiscount)
    },

    priceOne () {
      return this.allData.reduce(
        (acc, item) =>
          (item.isTeam ? this.getTeamPrice(item) : this.getPrice(item)) + acc,
        0
      )
    },

    priceTwo () {
      return this.allData.reduce(
        (acc, item) =>
          (item.isTeam
            ? this.getTeamPrice(item, true)
            : this.getPrice(item, true)) + acc,
        0
      )
    },

    total () {
      return this.allData.reduce(
        (acc, item) => {
          const priceWithTournamentDiscount = item.isTeam
            ? this.getTeamPrice(item, true)
            : this.getPrice(item, true)

          const priceWithoutTournamentDiscount = item.isTeam
            ? this.getTeamPrice(item, false)
            : this.getPrice(item, false)

          const deduction = this.getCostReduction(item.club)

          const itemPrice = deduction
            ? ((100 - deduction) / 100) * priceWithoutTournamentDiscount
            : priceWithTournamentDiscount

          return acc + itemPrice
        },
        0
      )
    }
  },

  methods: {
    getCostReduction (club) {
      if (!club?.id) return 0
      const clubDeductions = this.tournament.clubDeductions || []
      return clubDeductions.find(d => parseInt(d.clubId) === parseInt(club.id))?.deduction || 0
    },

    printit (discount) {
      this.hideDiscount = discount

      this.$nextTick(() => {
        this.openPrint(
          `Kotizacija${this.title}`,
          this.$refs.printTable ? this.$refs.printTable.$el : null
        )
      })
    },

    getCompetitorNames (item) {
      const keys = [
        'competitor',
        'competitorTwo',
        'competitorThree',
        'competitorFour'
      ]

      const competitors = keys
        .map((key) => item[key])
        .filter((item) => !!item)
        .map((competitor) => `${competitor.firstName} ${competitor.lastName}`)

      return competitors.join(', ')
    },

    getDisciplinesDataList (categories) {
      return categories?.reduce((acc, item) => {
        acc.push({
          label: item.category?.name
        })

        const discplines = item.disciplines?.map((disc) => ({
          text: disc.discipline.name
        }))

        acc.push(...discplines)

        return acc
      }, [])
    },

    getClubLabel (club) {
      return club?.shortName || club?.name || ''
    },

    getCompetitorDisplayName (row) {
      const c = row.competitor
      if (!c) return '—'
      const name = `${c.firstName || ''} ${c.lastName || ''}`.trim()
      return name || '—'
    },

    hasClubDiscount (row) {
      return !!(row.club && this.getCostReduction(row.club))
    }
  }
}
</script>

<style lang="scss" scoped>
.text {
  margin-top: 10px;
  text-align: right;
}
.flex {
  display: flex;
  align-items: center;
  gap: 8px;
}
.name {
  line-height: 1.6;
}

.club-name {
  font-size: 0.9em;
  color: #606266;
  margin-top: 2px;
}
</style>
